import React, { useEffect, useMemo, useState } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import { Skeleton } from "../../util/skeleton/Skeleton";
import { useStat } from "../../DataStore/StatContext";
import { useTheme } from "../../DataStore/ThemeContext";
import { Link, useNavigate } from "react-router-dom";
import FilterBar from "../../components/Common/FilterBar";

/** -----------------------------------------------
 *  Tiny, no-deps sparkline
 *  Theme: indigo
 * ----------------------------------------------*/
const Sparkline = ({
  data = [],
  w = 160,
  h = 46,
  stroke = "#4F46E5",
  fill = "rgba(79,70,229,.12)",
}) => {
  const safe = Array.isArray(data) && data.length ? data : [0, 0, 0, 0, 0, 0, 0];
  const max = Math.max(...safe, 1);
  const min = Math.min(...safe, 0);
  const dx = w / Math.max(1, safe.length - 1);
  const sy = (v) => h - ((v - min) / (max - min || 1)) * h;
  const path = safe.map((v, i) => `${i === 0 ? "M" : "L"} ${i * dx},${sy(v)}`).join(" ");
  const area = `${path} L ${w},${h} L 0,${h} Z`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <path d={area} fill={fill} />
      <path d={path} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
};

const nf = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/** -----------------------------------------------
 *  Status Pill (theme aligned)
 * ----------------------------------------------*/
const StatusPill = ({ status = "Active" }) => {
  const s = String(status || "").toLowerCase();
  const cls =
    s === "active"
      ? "yx-status yx-status--active"
      : s === "on-hold"
      ? "yx-status yx-status--hold"
      : "yx-status yx-status--closed";

  return <span className={cls}>{status}</span>;
};

/** -----------------------------------------------
 *  Project Card (grid)
 * ----------------------------------------------*/
const ProjectCard = ({ project, onManage, currency = "UGX" }) => {
  const income = Number(project.income || 0);
  const expenses = Number(project.expenses || 0);
  const budget = Number(project.budget || 0);

  const net = income - expenses;
  const margin = income > 0 ? Math.round((net / income) * 100) : 0;
  const util = budget > 0 ? Math.min(100, Math.round((expenses / budget) * 100)) : 0;

  const netTone = net < 0 ? "is-neg" : "is-pos";

  return (
    <div className="yx-projCard">
      <div className="yx-projCard__head">
        <div style={{ minWidth: 0 }}>
          <div className="yx-projCode">{project.code || "-"}</div>
          <div className="yx-projName" title={project.name}>
            {project.name}
          </div>
        </div>

        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <StatusPill status={project.status} />
        </div>
      </div>

      <div className="yx-projCard__spark">
        <Sparkline data={project.trend} w={220} h={54} />
      </div>

      <div className="yx-projGrid">
        <div className="yx-projMetric">
          <div className="yx-projMetric__label">Income</div>
          <div className="yx-projMetric__value">
            {currency} {nf(income)}
          </div>
        </div>

        <div className="yx-projMetric">
          <div className="yx-projMetric__label">Expense</div>
          <div className={`yx-projMetric__value ${net < 0 ? "is-danger" : ""}`}>
            {currency} {nf(expenses)}
          </div>
        </div>

        <div className="yx-projMetric text-right">
          <div className="yx-projMetric__label">Margin</div>
          <span className={`yx-badge ${netTone}`}>
            <i className={`fa ${net < 0 ? "fa-arrow-down" : "fa-arrow-up"}`} />
            {margin}%
          </span>
        </div>
      </div>

      <div className="yx-projBudget">
        <div className="d-flex justify-content-between">
          <div className="yx-projMetric__label">Budget</div>
          <div className="yx-projMetric__label">
            {currency} {nf(budget)}
          </div>
        </div>
        <div className="yx-bar mt-2">
          <div className="yx-bar__fill yx-bar__fill--indigo" style={{ width: `${util}%` }} />
        </div>
        <div className="d-flex justify-content-between mt-2">
          <div className="yx-projMetric__label">Utilization</div>
          <div className="yx-projMetric__value" style={{ fontSize: 13 }}>
            {util}%
          </div>
        </div>
      </div>

      <div className="yx-projActions">
        <Link className="yx-btn yx-btn--soft" to={`/project-income-report/${project.id}`}>
          <i className="fa fa-chart-line" />
          Report
        </Link>

        <button className="yx-btn yx-btn--indigo" onClick={() => onManage(project)} type="button">
          <i className="fa fa-pen" />
          Manage
        </button>
      </div>
    </div>
  );
};

const ProjectsPage = () => {
  const { getProjectStatList, projectStatList, loadingProjectStatList } = useStat();
  const { openSettings } = useTheme();
  const navigate = useNavigate();

  const loading = loadingProjectStatList ?? !Array.isArray(projectStatList);

  useEffect(() => {
    getProjectStatList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [sort, setSort] = useState("name");

  const currency = "USh";

  const projects = useMemo(() => {
    return Array.isArray(projectStatList)
      ? projectStatList.map((p) => ({
          id: p.id,
          code: p.project_code || "",
          name: p.tag_name || "",
          status: p.status || "Active",
          budget: Number(p.budget_amount || 0),
          income: Number(p.financials?.total_income || 0),
          expenses: Number(p.financials?.total_expense || 0),
          trend: Array.isArray(p.financials?.graph) ? p.financials.graph : [],
          description: p.description || "",
        }))
      : [];
  }, [projectStatList]);

  const filtered = useMemo(() => {
    let out = [...projects];

    if (status !== "All") {
      out = out.filter(
        (p) => String(p.status || "").toLowerCase() === String(status).toLowerCase()
      );
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter(
        (p) =>
          String(p.name || "").toLowerCase().includes(q) ||
          String(p.code || "").toLowerCase().includes(q)
      );
    }

    out.sort((a, b) => {
      if (sort === "name") return String(a.name || "").localeCompare(String(b.name || ""));
      if (sort === "budget") return Number(b.budget || 0) - Number(a.budget || 0);
      if (sort === "margin") {
        const am = a.income ? (a.income - a.expenses) / a.income : -Infinity;
        const bm = b.income ? (b.income - b.expenses) / b.income : -Infinity;
        return bm - am;
      }
      return 0;
    });

    return out;
  }, [projects, status, query, sort]);

  const handleNew = () => openSettings("Create New Project", "NewTag", null, "Create Project");
  const handleManage = (p) => openSettings("Manage Project", "ManageTag", p, "Save Project Changes");

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Projects"
          description="Create, track and manage your projects income and expenditures."
          iconClass="fa fa-tag"
        />

        {/* ✅ FilterBar replaces toolbar */}
        <FilterBar
          title="Projects"
          subtitle="Search, filter and sort your projects"
          controls={[
            {
              type: "search",
              value: query,
              placeholder: "Search by name or code…",
              width: 320,
              onChange: (e) => setQuery(e.target.value),
              onEnter: () => {}, // filtering happens reactively
            },
            {
              type: "select",
              value: status,
              placeholder: "Select Status...",
              width: 170,
              options: [
                { value: "All", label: "All" },
                { value: "Active", label: "Active" },
                { value: "On-Hold", label: "On-Hold" },
                { value: "Closed", label: "Closed" },
              ],
              onChange: (e) => setStatus(e.target.value),
            },
            {
              type: "select",
              value: sort,
              placeholder: "Sort",
              width: 170,
              options: [
                { value: "name", label: "Sort: Name" },
                { value: "budget", label: "Sort: Budget" },
                { value: "margin", label: "Sort: Margin" },
              ],
              onChange: (e) => setSort(e.target.value),
            },
          ]}
          actions={[
          
            {
              label: "New Project",
              iconClass: "fa fa-plus",
              onClick: handleNew,
              variant: "primary",
            },
          ]}
        />

        {/* Grid */}
        <div className="yx-card mb-3">
          <div className="yx-card-body">
            {loading ? (
              <div className="row">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="col-md-6 col-lg-4 mb-3">
                    <div className="yx-projCard" style={{ opacity: 0.85 }}>
                      <div className="yx-projCard__head">
                        <div style={{ flex: 1 }}>
                          <Skeleton w="40%" h={12} />
                          <Skeleton w="75%" h={16} style={{ marginTop: 8 }} />
                        </div>
                        <Skeleton w="72px" h={26} style={{ borderRadius: 999 }} />
                      </div>
                      <div className="yx-projCard__spark">
                        <Skeleton w="100%" h={54} style={{ borderRadius: 12 }} />
                      </div>
                      <div style={{ padding: 16 }}>
                        <Skeleton w="100%" h={10} style={{ borderRadius: 999 }} />
                        <div className="d-flex justify-content-between mt-3">
                          <Skeleton w="35%" h={14} />
                          <Skeleton w="28%" h={14} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="row">
                {filtered.map((p) => (
                  <div key={p.id} className="col-md-6 col-lg-4 mb-3">
                    <ProjectCard project={p} onManage={handleManage} currency={currency} />
                  </div>
                ))}
                {filtered.length === 0 && (
                  <div className="col-12 text-center yx-muted py-4">
                    No projects match your filters.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default ProjectsPage;
